import { Box, Flex, Link, Text } from '@chakra-ui/react';
import { useLocation } from 'react-router-dom';
import { SupportDonationButton } from '../SupportDonationButton';
import { APP_FRAME_MAX_W, APP_FRAME_PX } from './layoutConstants';

export interface AppFooterProps {
  /** Full-width strip below the page (default). Narrow column when placed under filters. */
  variant?: 'full' | 'sidebar';
}

// Required Riot Legal Jibber Jabber notice. Per the Riftbound digital-tools
// policy this must be present in a place that's easy to find.
export function AppFooter({ variant = 'full' }: AppFooterProps) {
  const isSidebar = variant === 'sidebar';
  const location = useLocation();
  /** Tool-style routes use the full viewport band; skip the default marketing footer offset. */
  const flushTop = location.pathname === '/card-print';

  const body = (
    <>
      <Text fontSize="xs" color="textMuted" lineHeight="1.6">
        {`Riftbound DB was created under Riot Games' "`}
        <Link
          href="https://www.riotgames.com/en/legal"
          target="_blank"
          rel="noopener noreferrer"
        >
          Legal Jibber Jabber
        </Link>
        {`" policy using assets owned by Riot Games. Riot Games does not endorse or sponsor this project.`}
      </Text>
      <Text fontSize="xs" color="textMuted" mt={2}>
        Browse:{' '}
        <Link href="/sets">Sets</Link> ·{' '}
        <Link href="/legends">Legends</Link> ·{' '}
        <Link href="/errata">Errata</Link>
      </Text>
      <Text fontSize="xs" color="textMuted" mt={1}>
        Card data via the{' '}
        <Link href="https://developer.riotgames.com/docs/riftbound" target="_blank">
          Riftbound API
        </Link>
        . ·{' '}
        <Link href="/terms">Terms</Link> ·{' '}
        <Link href="/privacy">Privacy</Link> ·{' '}
        <Link href="/about">About</Link>
      </Text>
    </>
  );

  const supportCta = (
    <Box
      flexShrink={0}
      w={{ base: '100%', md: 'auto' }}
      maxW={{ md: '220px' }}
      alignSelf={{ base: 'stretch', md: 'flex-start' }}
    >
      {/* Slim icon chip on mobile (full-width); branded graphic stays heavy — use compact row everywhere in footer */}
      <SupportDonationButton layout="inline" />
    </Box>
  );

  if (isSidebar) {
    // Narrow columns (deck builder, filter sidebars): always stack — button full width under copy.
    // px={3} matches section / filter padding above (e.g. DeckPanel Section).
    return (
      <Box as="footer" px={3} py={2}>
        <Flex direction="column" gap={3} align="stretch" w="100%">
          <Box minW={0}>{body}</Box>
          <Box w="100%" alignSelf="stretch">
            <SupportDonationButton layout="inline" />
          </Box>
        </Flex>
      </Box>
    );
  }

  return (
    <Box
      as="footer"
      bg="bgElevated"
      borderTopWidth="hairline"
      borderColor="border"
      mt={flushTop ? 0 : 12}
      py={flushTop ? 3 : 6}
    >
      <Box maxW={APP_FRAME_MAX_W} mx="auto" px={APP_FRAME_PX}>
        <Flex
          direction={{ base: 'column', md: 'row' }}
          gap={{ base: 4, md: 8 }}
          justify="space-between"
          align={{ base: 'stretch', md: 'flex-start' }}
        >
          <Box flex={1} minW={0}>
            {body}
          </Box>
          {supportCta}
        </Flex>
      </Box>
    </Box>
  );
}
